<template>
  <div class="min-h-screen bg-gradient-to-b from-gray-50 to-white">
    <!-- Reading Progress Bar -->
    <div
      class="fixed top-0 left-0 h-1 bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700 transition-all duration-300 z-50"
      :style="{ width: readingProgress + '%' }"
    ></div>

    <!-- Floating Back Button - Apple Style -->
    <div class="fixed top-24 left-6 z-40">
      <router-link
        to="/"
        class="back-button-floating group"
        :class="{ scrolled: readingProgress > 5 }"
      >
        <svg
          class="w-5 h-5 transform group-hover:-translate-x-1 transition-transform duration-300"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2.5"
            d="M15 19l-7-7 7-7"
          ></path>
        </svg>
        <span class="back-button-text">Home</span>
      </router-link>
    </div>

    <div v-if="loading" class="flex justify-center py-12">
      <div class="spinner"></div>
    </div>

    <article v-else-if="post" class="relative">
      <!-- Hero Image Section -->
      <div
        v-if="post.image_url"
        class="relative h-[500px] mb-12 overflow-hidden"
      >
        <div
          class="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-transparent z-10"
        ></div>
        <img
          :src="post.image_url"
          :alt="post.title"
          class="w-full h-full object-cover"
        />

        <!-- Floating Header on Image -->
        <div class="absolute bottom-0 left-0 right-0 z-20 p-8 md:p-12">
          <div class="max-w-4xl mx-auto">
            <!-- Theme Badge -->
            <!-- <span
              class="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-white/90 backdrop-blur-sm text-gray-900 shadow-lg mb-4"
            >
              <span class="w-2 h-2 rounded-full bg-primary-500 mr-2"></span>
              {{ post.theme }}
            </span> -->

            <h1
              class="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 drop-shadow-2xl leading-tight"
            >
              {{ post.title }}
            </h1>
          </div>
        </div>
      </div>

      <!-- Content Container -->
      <div class="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
        <!-- Header (quando não há imagem) -->
        <header v-if="!post.image_url" class="mb-12">
          <span
            class="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-primary-50 text-primary-700 mb-6"
          >
            <span class="w-2 h-2 rounded-full bg-primary-500 mr-2"></span>
            {{ post.theme }}
          </span>

          <h1
            class="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight"
          >
            {{ post.title }}
          </h1>
        </header>

        <!-- Author & Meta Info -->
        <div
          class="flex items-center justify-between mb-12 pb-8 border-b border-gray-200"
        >
          <div class="flex items-center space-x-4">
            <UserAvatar :user="post.author" size="lg" />
            <div>
              <p class="font-semibold text-gray-900 text-lg">
                {{ post.author.username }}
              </p>
              <div class="flex items-center space-x-3 text-sm text-gray-600">
                <span>{{ formatDate(post.created_at) }}</span>
                <span class="text-gray-400">•</span>
                <span
                  >{{ calculateReadingTime(post.content) }} min de leitura</span
                >
              </div>
            </div>
          </div>

          <!-- Floating Action Bar (Desktop) -->
          <div class="hidden lg:flex items-center space-x-4">
            <!-- Like Button -->
            <button
              @click="toggleLike"
              :disabled="!authStore.isAuthenticated || animatingLike"
              class="action-btn-float"
              :class="[
                { active: post.userInteractions?.liked },
                { 'animate-like': animatingLike },
              ]"
            >
              <div class="relative">
                <HeartIcon
                  class="w-5 h-5 transition-all duration-300"
                  :class="[
                    {
                      'fill-current text-red-500': post.userInteractions?.liked,
                    },
                    { 'text-red-600': animatingLike },
                  ]"
                />
                <!-- Particles Container -->
                <div v-if="animatingLike" class="like-particles">
                  <div
                    v-for="i in 6"
                    :key="i"
                    class="like-particle"
                    :style="{
                      '--particle-direction': `translate(${
                        Math.cos((i * 60 * Math.PI) / 180) * 40
                      }px, ${Math.sin((i * 60 * Math.PI) / 180) * 40}px)`,
                      animationDelay: `${i * 0.05}s`,
                    }"
                  ></div>
                </div>
              </div>
              <span class="font-medium">{{ post.stats.likes }}</span>
            </button>

            <!-- Save Button -->
            <button
              @click="toggleSave"
              :disabled="!authStore.isAuthenticated || animatingSave"
              class="action-btn-float"
              :class="[
                { active: post.userInteractions?.saved },
                { 'animate-save': animatingSave },
              ]"
            >
              <BookmarkIcon
                class="w-5 h-5 transition-all duration-300"
                :class="[
                  {
                    'fill-current text-primary-500':
                      post.userInteractions?.saved,
                  },
                ]"
              />
            </button>
          </div>
        </div>

        <!-- Post Content - Magazine Style -->
        <div class="post-content-container mb-16">
          <div
            v-html="formatContent(post.content)"
            class="post-content-modern"
            ref="contentRef"
          ></div>
        </div>

        <!-- Mobile Actions Bar -->
        <div
          class="lg:hidden sticky bottom-0 left-0 right-0 bg-white/90 backdrop-blur-lg border-t border-gray-200 p-4 shadow-lg z-40"
        >
          <div class="flex items-center justify-around max-w-md mx-auto">
            <!-- Like Button -->
            <button
              @click="toggleLike"
              :disabled="!authStore.isAuthenticated || animatingLike"
              class="action-btn flex items-center space-x-2 text-gray-600 hover:text-red-600 transition-all duration-300 relative overflow-visible"
              :class="[
                { 'text-red-600': post.userInteractions?.liked },
                { 'animate-like': animatingLike },
              ]"
            >
              <div class="relative">
                <HeartIcon
                  class="w-5 h-5 transition-all duration-300"
                  :class="[
                    {
                      'fill-current text-red-500': post.userInteractions?.liked,
                    },
                    { 'text-red-600': animatingLike },
                  ]"
                />
                <!-- Particles Container -->
                <div v-if="animatingLike" class="like-particles">
                  <div
                    v-for="i in 6"
                    :key="i"
                    class="like-particle"
                    :style="{
                      '--particle-direction': `translate(${
                        Math.cos((i * 60 * Math.PI) / 180) * 40
                      }px, ${Math.sin((i * 60 * Math.PI) / 180) * 40}px)`,
                      animationDelay: `${i * 0.05}s`,
                    }"
                  ></div>
                </div>
              </div>
              <span class="transition-all duration-300 font-medium">{{
                post.stats.likes
              }}</span>
            </button>

            <!-- Save Button -->
            <button
              @click="toggleSave"
              :disabled="!authStore.isAuthenticated || animatingSave"
              class="action-btn flex items-center space-x-2 text-gray-600 hover:text-primary-600 transition-all duration-300"
              :class="[
                { 'text-primary-600': post.userInteractions?.saved },
                { 'animate-save': animatingSave },
              ]"
            >
              <BookmarkIcon
                class="w-5 h-5 transition-all duration-300"
                :class="[
                  {
                    'fill-current text-primary-500':
                      post.userInteractions?.saved,
                  },
                ]"
              />
              <span class="transition-all duration-300 font-medium">{{
                post.userInteractions?.saved ? "Salvo" : "Salvar"
              }}</span>
            </button>

            <!-- Comments Counter -->
            <div class="flex items-center space-x-2 text-gray-600">
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                ></path>
              </svg>
              <span class="font-medium">{{ post.stats.comments }}</span>
            </div>
          </div>
        </div>
      </div>
    </article>

    <!-- Comments Section -->
    <div v-if="post" class="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 mt-16 mb-24">
      <div class="border-t-2 border-gray-200 pt-12">
        <h3 class="text-3xl font-bold text-gray-900 mb-8 flex items-center">
          <svg
            class="w-8 h-8 mr-3 text-primary-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            ></path>
          </svg>
          Comentários
          <span class="ml-3 text-lg font-normal text-gray-500"
            >({{ comments.length }})</span
          >
        </h3>

        <!-- Add Comment (if authenticated) -->
        <div
          v-if="authStore.isAuthenticated"
          class="bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-8"
        >
          <form @submit.prevent="addComment" class="space-y-4">
            <textarea
              ref="commentTextarea"
              v-model="newComment"
              data-tutorial="comment-input"
              placeholder="Compartilhe suas ideias sobre este post..."
              class="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none transition-all"
              rows="4"
              required
            ></textarea>
            <div class="flex justify-end">
              <button
                type="submit"
                :disabled="!newComment.trim() || submittingComment"
                class="btn-primary px-6 py-3 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {{ submittingComment ? "Enviando..." : "Publicar Comentário" }}
              </button>
            </div>
          </form>
        </div>

        <!-- Login to Comment -->
        <div
          v-else
          class="text-center py-8 px-6 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-300 mb-8"
        >
          <p class="text-gray-600 mb-4">Faça login para comentar neste post</p>
          <router-link to="/login" class="btn-primary inline-block">
            Fazer Login
          </router-link>
        </div>

        <!-- Comments List -->
        <div v-if="comments.length > 0" class="space-y-6">
          <CommentThread
            v-for="comment in comments"
            :key="comment.id"
            :comment="comment"
            :post-id="post.id"
            :depth="0"
            @reply="handleReply"
            @edit="handleCommentEdit"
            @like="handleCommentLike"
          />
        </div>

        <div v-else class="text-center py-12 px-6">
          <div class="max-w-md mx-auto">
            <svg
              class="w-16 h-16 mx-auto text-gray-300 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              ></path>
            </svg>
            <p class="text-gray-600 text-lg">Nenhum comentário ainda.</p>
            <p class="text-gray-500 mt-2">
              Seja o primeiro a compartilhar suas ideias!
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Not Found -->
    <div
      v-if="!loading && !post"
      class="text-center py-12 max-w-md mx-auto px-4"
    >
      <svg
        class="w-24 h-24 mx-auto text-gray-300 mb-6"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        ></path>
      </svg>
      <h2 class="text-3xl font-bold text-gray-900 mb-4">Post não encontrado</h2>
      <p class="text-gray-600 mb-6">
        O post que você está procurando não existe ou foi removido.
      </p>
      <router-link to="/" class="btn-primary inline-block px-6 py-3 rounded-xl">
        Voltar ao Início
      </router-link>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, nextTick, watch } from "vue";
import { useRoute } from "vue-router";
import { useAuthStore } from "@/store/auth";
import apiService from "@/services/api";
import { HeartIcon, BookmarkIcon } from "lucide-vue-next";
import { markdownToHtml } from "@/utils/contentUtils";
import Prism from "prismjs";
import CommentThread from "@/components/CommentThread.vue";
import UserAvatar from "@/components/UserAvatar.vue";

export default {
  name: "PostDetail",
  components: {
    HeartIcon,
    BookmarkIcon,
    CommentThread,
    UserAvatar,
  },
  setup() {
    const route = useRoute();
    const authStore = useAuthStore();

    const post = ref(null);
    const comments = ref([]);
    const loading = ref(true);
    const newComment = ref("");
    const submittingComment = ref(false);
    const contentRef = ref(null);
    const commentTextarea = ref(null); // Ref para detectar comentários do tutorial
    const readingProgress = ref(0);

    // Animation states
    const animatingLike = ref(false);
    const animatingSave = ref(false);

    // Reading Progress Tracker
    const updateReadingProgress = () => {
      const windowHeight = window.innerHeight;
      const documentHeight =
        document.documentElement.scrollHeight - windowHeight;
      const scrolled = window.scrollY;
      const progress = (scrolled / documentHeight) * 100;
      readingProgress.value = Math.min(100, Math.max(0, progress));
    };

    // ✅ Função helper para normalizar comentários recursivamente
    const normalizeComment = (comment) => {
      return {
        ...comment,
        stats: {
          likes: comment.stats?.likes || 0,
        },
        userInteractions: {
          liked: comment.userInteractions?.liked || false,
        },
        replies: (comment.replies || []).map(normalizeComment), // Recursivo para replies
      };
    };

    const fetchPost = async () => {
      try {
        console.log("📤 Carregando post:", route.params.id);

        const response = await apiService.get("/api/handlers", {
          params: {
            module: "posts",
            action: "detail",
            id: route.params.id,
          },
        });

        console.log("📥 Resposta recebida:", response);

        // 🛡️ Validação defensiva
        if (response.data?.data?.post) {
          post.value = response.data.data.post;
        } else if (response.data?.post) {
          post.value = response.data.post;
        } else if (response.data) {
          post.value = response.data;
        } else {
          throw new Error("Formato de resposta inválido");
        }

        // ✅ CORRIGIDO: Comentários vêm dentro de post.comments e normaliza recursivamente
        comments.value = (post.value.comments || []).map(normalizeComment);
        console.log(
          `📝 Post carregado com ${comments.value.length} comentários`
        );
      } catch (error) {
        console.error("Erro ao carregar post:", error);
      } finally {
        loading.value = false;
      }
    };

    const toggleLike = async () => {
      if (!authStore.isAuthenticated || animatingLike.value) return;

      animatingLike.value = true;

      // Optimistic UI Update - atualiza imediatamente
      const wasLiked = post.value.userInteractions?.liked;
      const previousLikes = post.value.stats.likes;

      console.log("❤️ [TOGGLE LIKE] Estado anterior:", {
        wasLiked,
        previousLikes,
      });

      // Atualiza UI instantaneamente
      if (!post.value.userInteractions) {
        post.value.userInteractions = {};
      }
      post.value.userInteractions.liked = !wasLiked;
      post.value.stats.likes = wasLiked ? previousLikes - 1 : previousLikes + 1;

      console.log("❤️ [TOGGLE LIKE] Estado otimista:", {
        liked: post.value.userInteractions.liked,
        likes: post.value.stats.likes,
      });

      try {
        console.log("❤️ [TOGGLE LIKE] Enviando requisição...");
        const response = await apiService.post(
          `/api/posts/${route.params.id}/like`
        );
        console.log("❤️ [TOGGLE LIKE] Resposta recebida:", response.data);

        // Revalidar dados em background (sem bloquear UI)
        console.log("❤️ [TOGGLE LIKE] Revalidando dados...");
        await fetchPost();
        console.log("❤️ [TOGGLE LIKE] Dados revalidados:", {
          liked: post.value.userInteractions.liked,
          likes: post.value.stats.likes,
        });
      } catch (error) {
        console.error("❌ [TOGGLE LIKE] Erro:", error);
        // Reverte em caso de erro
        post.value.userInteractions.liked = wasLiked;
        post.value.stats.likes = previousLikes;
      } finally {
        // Remove animation after particles complete
        setTimeout(() => {
          animatingLike.value = false;
        }, 700);
      }
    };

    const toggleSave = async () => {
      if (!authStore.isAuthenticated || animatingSave.value) return;

      animatingSave.value = true;

      // Optimistic UI Update - atualiza imediatamente
      const wasSaved = post.value.userInteractions?.saved;

      // Atualiza UI instantaneamente
      if (!post.value.userInteractions) {
        post.value.userInteractions = {};
      }
      post.value.userInteractions.saved = !wasSaved;

      try {
        await apiService.post("/api/handlers", null, {
          params: {
            module: "posts",
            action: "save",
            id: route.params.id,
          },
        });
        // Revalidar dados em background (sem bloquear UI)
        await fetchPost();
      } catch (error) {
        console.error("Erro ao alterar salvamento:", error);
        // Reverte em caso de erro
        post.value.userInteractions.saved = wasSaved;
      } finally {
        // Remove animation after a brief delay
        setTimeout(() => {
          animatingSave.value = false;
        }, 500);
      }
    };

    const addComment = async () => {
      if (!newComment.value.trim()) return;

      // Detectar se é um comentário de tutorial
      const isTutorialComment =
        commentTextarea.value?.dataset?.tutorialComment === "true";

      if (isTutorialComment) {
        console.log("🎯 Tutorial: Comentário simulado (não será salvo)");

        // Simular envio com feedback visual
        submittingComment.value = true;

        // Aguardar um momento para dar feedback
        await new Promise((resolve) => setTimeout(resolve, 800));

        // Limpar campo
        newComment.value = "";

        // Remover flag de tutorial
        if (commentTextarea.value) {
          delete commentTextarea.value.dataset.tutorialComment;
        }

        submittingComment.value = false;

        // Mostrar mensagem de sucesso do tutorial
        console.log("✅ Tutorial: Comentário simulado com sucesso!");

        return;
      }

      // Fluxo normal de comentário
      submittingComment.value = true;
      try {
        await apiService.post(
          "/api/handlers",
          {
            content: newComment.value.trim(),
          },
          {
            params: {
              module: "posts",
              action: "comments",
              id: route.params.id,
            },
          }
        );
        newComment.value = "";
        // Refresh comments
        await fetchPost();
      } catch (error) {
        console.error("Erro ao adicionar comentário:", error);
      } finally {
        submittingComment.value = false;
      }
    };

    const toggleCommentLike = async (comment, postId) => {
      // Impedir self-liking no frontend também (extra validação)
      if (isUserOwnComment(comment)) return;

      console.log(`👍 Toggling like for comment ${comment.id}`);

      // Optimistic UI update
      const wasLiked = comment.userInteractions?.liked;
      const previousLikes = comment.stats.likes;

      // Atualiza comentário localmente
      if (!comment.userInteractions) {
        comment.userInteractions = {};
      }
      comment.userInteractions.liked = !wasLiked;
      comment.stats.likes = wasLiked ? previousLikes - 1 : previousLikes + 1;

      try {
        await apiService.post(
          `/api/posts/${postId}/comments/${comment.id}/like`
        );
        // Refresh em background
        fetchPost();
      } catch (error) {
        console.error("Erro ao alterar like do comentário:", error);
        // Reverte mudanças em caso de erro
        comment.userInteractions.liked = wasLiked;
        comment.stats.likes = previousLikes;
      }
    };

    const isUserOwnComment = (comment) => {
      return comment.author.username === authStore.user?.username;
    };

    const handleReply = async (data) => {
      const { content, parentCommentId } = data;
      try {
        await apiService.post(
          `/api/posts/${route.params.id}/comments/${parentCommentId}/reply`,
          {
            content,
          }
        );
        // Refresh comments após adicionar reply
        await fetchPost();
      } catch (error) {
        console.error("Erro ao adicionar resposta:", error);
      }
    };

    const handleCommentEdit = async (data) => {
      const { commentId, content } = data;
      console.log("🔄 handleCommentEdit chamado:", { commentId, content });

      let optimisticUpdateSuccess = false;

      try {
        // Update otimista: atualiza o comentário localmente primeiro
        optimisticUpdateSuccess = updateCommentContent(commentId, content);
        console.log("✅ Update otimista executado:", optimisticUpdateSuccess);

        if (!optimisticUpdateSuccess) {
          console.error("❌ ERR: Falha no update otimista, abortando edição");
          return;
        }

        // Faz a requisição para salvar no backend
        await apiService.put(
          `/api/posts/${route.params.id}/comments/${commentId}`,
          {
            content: content.trim(),
          }
        );

        console.log("✅ Comentário editado com sucesso no backend");

        // Pequena pausa para garantir que a UI seja atualizada
        setTimeout(() => {
          console.log(
            "🔄 Refetching comments após edição para garantir consistência"
          );
          // Refetch para garantir que todas as replies aninhadas estejam corretas
          fetchPost();
        }, 100);
      } catch (error) {
        console.error("❌ Erro ao editar comentário:", error);
        // Em caso de erro, poderíamos reverter o update otimista aqui
        // Mas por ora apenas logamos o erro - o update já foi feito localmente
      }
    };

    const updateCommentContent = (commentId, newContent) => {
      console.log(
        "🔍 Procurando commentId:",
        commentId,
        "nos comentários:",
        comments.value.length
      );

      const updateInArray = (commentsArr) => {
        if (!commentsArr || !Array.isArray(commentsArr)) {
          console.log("❌ Array vazio ou inválido");
          return false;
        }

        for (let i = 0; i < commentsArr.length; i++) {
          const comment = commentsArr[i];

          if (!comment || !comment.id) {
            console.log("❌ Comentário inválido no índice", i);
            continue;
          }

          console.log(
            "🔄 Verificando comentário:",
            comment.id,
            "vs",
            commentId
          );

          if (comment.id === commentId) {
            console.log("✅ Encontrado comentário, atualizando...");
            console.log(
              "📝 Conteúdo antigo:",
              comment.content?.substring(0, 50) + "..."
            );
            console.log(
              "📝 Conteúdo novo:",
              newContent?.substring(0, 50) + "..."
            );

            // Criar novo objeto mantendo todas as propriedades e atualizando apenas content
            const updatedComment = {
              ...comment,
              content: newContent.trim(),
              updated_at: new Date().toISOString(), // Atualizar timestamp local
            };

            // Forçar reatividade usando Vue.set ou splice
            commentsArr.splice(i, 1, updatedComment);

            console.log(
              "✅ Atualização aplicada - novo conteúdo:",
              updatedComment.content?.substring(0, 50) + "..."
            );

            // Trigger reatividade imediata
            nextTick(() => {
              console.log(
                "🔍 Estado final verificado:",
                commentsArr[i]?.content?.substring(0, 50) + "..."
              );
            });

            return true;
          }

          // Recursivamente verifica replies (até 3 níveis de profundidade)
          if (
            comment.replies &&
            Array.isArray(comment.replies) &&
            comment.replies.length > 0
          ) {
            console.log(
              "🔄 Verificando replies de:",
              comment.id,
              "(" + comment.replies.length + " replies)"
            );
            if (updateInArray(comment.replies)) return true;
          }
        }
        return false;
      };

      const result = updateInArray(comments.value);
      console.log("🔍 Resultado da busca:", result);

      if (!result) {
        console.error("❌ ERRO: Comentário não encontrado para atualização!", {
          commentId,
          newContent: newContent?.substring(0, 50),
        });
      }

      return result;
    };

    const handleCommentLike = async (data) => {
      const { comment, postId } = data;
      await toggleCommentLike(comment, postId);
    };

    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleDateString("pt-BR", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    };

    const formatContent = (content) => {
      if (!content) return "<p>Conteúdo não disponível.</p>";

      // Processar markdown - função síncrona
      return markdownToHtml(content);
    };

    const calculateReadingTime = (content) => {
      if (!content) return 0;
      const wordsPerMinute = 200; // Velocidade média de leitura
      const words = content.split(/\s+/).length;
      return Math.max(1, Math.ceil(words / wordsPerMinute));
    };

    const calculateWordCount = (content) => {
      if (!content) return 0;
      return content.split(/\s+/).length;
    };

    // Função para copiar código para a área de transferência
    const copyCodeToClipboard = (element) => {
      const codeElement = element.closest("pre").querySelector("code");
      const codeText = codeElement.textContent || codeElement.innerText;

      // Adicionar classe de feedback visual
      element.classList.add("copied");

      navigator.clipboard
        .writeText(codeText)
        .then(() => {
          // Classe 'copied' permanece por 2 segundos
          setTimeout(() => {
            element.classList.remove("copied");
          }, 2000);
        })
        .catch((err) => {
          console.error("Erro ao copiar código:", err);
          element.classList.remove("copied");
          // Fallback para copiar não funcional
          alert("Não foi possível copiar o código. Copie manualmente.");
        });
    };

    // Watcher para aplicar syntax highlighting quando o post é carregado
    watch(
      () => post.value,
      async (newPost) => {
        if (newPost) {
          // Esperar a próxima DOM update para aplicar o highlight
          await nextTick();
          try {
            setTimeout(() => {
              if (contentRef.value) {
                // Aplicar syntax highlighting
                Prism.highlightAllUnder(contentRef.value);

                // Adicionar funcionalidade de copiar para todos os blocos de código
                setTimeout(() => {
                  if (contentRef.value) {
                    const codeBlocks = contentRef.value.querySelectorAll("pre");
                    codeBlocks.forEach((block) => {
                      block.addEventListener("click", (e) => {
                        // Verificar se o clique foi na área do botão de cópia
                        const rect = block.getBoundingClientRect();
                        const x = e.clientX - rect.left;
                        const y = e.clientY - rect.top;

                        // O botão fica no top-right (aprox 10px de margem)
                        if (x >= rect.width - 30 && y <= 30) {
                          copyCodeToClipboard(block);
                        }
                      });
                    });
                  }
                }, 200);
              }
            }, 100);
          } catch (error) {
            console.warn("Erro ao aplicar syntax highlighting:", error);
          }
        }
      }
    );

    onMounted(() => {
      fetchPost();
      window.addEventListener("scroll", updateReadingProgress);
      updateReadingProgress();
    });

    onUnmounted(() => {
      window.removeEventListener("scroll", updateReadingProgress);
    });

    return {
      authStore,
      post,
      comments,
      loading,
      newComment,
      submittingComment,
      animatingLike,
      animatingSave,
      contentRef,
      readingProgress,
      toggleLike,
      toggleSave,
      addComment,
      toggleCommentLike,
      isUserOwnComment,
      handleReply,
      handleCommentEdit,
      handleCommentLike,
      formatDate,
      formatContent,
      calculateReadingTime,
      calculateWordCount,
    };
  },
};
</script>

<style scoped>
/* Magazine-style Content */
.post-content-modern {
  font-family: "Georgia", "Times New Roman", serif;
  font-size: 1.125rem;
  line-height: 1.8;
  color: #1a1a1a;
  letter-spacing: -0.003em;
  overflow-x: hidden;
  max-width: 100%;
}

.post-content-modern :deep(h1),
.post-content-modern :deep(h2),
.post-content-modern :deep(h3) {
  font-family: "Inter", system-ui, -apple-system, sans-serif;
  font-weight: 700;
  letter-spacing: -0.02em;
  margin-top: 2.5rem;
  margin-bottom: 1.25rem;
  color: #111827;
}

.post-content-modern :deep(h1) {
  font-size: 2.5rem;
  line-height: 1.2;
}

.post-content-modern :deep(h2) {
  font-size: 2rem;
  line-height: 1.3;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 0.5rem;
}

.post-content-modern :deep(h3) {
  font-size: 1.5rem;
  line-height: 1.4;
}

.post-content-modern :deep(p) {
  margin-bottom: 1.75rem;
  text-align: justify;
  hyphens: auto;
}

.post-content-modern :deep(strong) {
  font-weight: 600;
  color: #111827;
}

.post-content-modern :deep(em) {
  font-style: italic;
  color: #374151;
}

.post-content-modern :deep(ul),
.post-content-modern :deep(ol) {
  margin-bottom: 1.75rem;
  padding-left: 1.75rem;
}

.post-content-modern :deep(li) {
  margin-bottom: 0.75rem;
  line-height: 1.7;
}

.post-content-modern :deep(blockquote) {
  border-left: 4px solid #3b82f6;
  padding-left: 1.5rem;
  margin: 2rem 0;
  font-style: italic;
  color: #4b5563;
  background: #f9fafb;
  padding: 1.5rem;
  border-radius: 0.5rem;
}

/* Table responsive wrapper */
.post-content-modern :deep(.table-responsive-wrapper) {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin: 2rem 0;
  max-width: 100%;
  width: 100%;
}

/* Table styles - professional magazine look */
.post-content-modern :deep(table) {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin: 0;
  font-family: "Inter", system-ui, -apple-system, sans-serif;
  font-size: 0.9rem;
  line-height: 1.6;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06);
  min-width: 600px;
}

.post-content-modern :deep(thead) {
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
}

.post-content-modern :deep(th) {
  padding: 0.875rem 1.25rem;
  font-weight: 600;
  font-size: 0.8125rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #f1f5f9;
  border: none;
  border-bottom: 2px solid #475569;
  text-align: left;
}

.post-content-modern :deep(td) {
  padding: 0.875rem 1.25rem;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
  vertical-align: top;
  font-family: "Georgia", "Times New Roman", serif;
  font-size: 0.9375rem;
}

@media (max-width: 768px) {
  .post-content-modern :deep(th),
  .post-content-modern :deep(td) {
    padding: 0.625rem 0.75rem;
    font-size: 0.8125rem;
  }
  .post-content-modern :deep(table) {
    font-size: 0.8rem;
  }
}

.post-content-modern :deep(tr:last-child td) {
  border-bottom: none;
}

.post-content-modern :deep(tbody tr) {
  transition: background-color 0.15s ease;
}

.post-content-modern :deep(tbody tr:nth-child(even)) {
  background-color: #f8fafc;
}

.post-content-modern :deep(tbody tr:hover) {
  background-color: #eff6ff;
}

.post-content-modern :deep(td strong) {
  color: #111827;
  font-weight: 600;
}

.post-content-modern :deep(code) {
  background: #f3f4f6;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-family: "Monaco", "Courier New", monospace;
  font-size: 0.9em;
  color: #dc2626;
}

.post-content-modern :deep(pre) {
  background: #1f2937;
  padding: 1.5rem;
  border-radius: 0.75rem;
  overflow-x: auto;
  margin: 2rem 0;
}

.post-content-modern :deep(pre code) {
  background: transparent;
  padding: 0;
  color: #f9fafb;
}

/* First letter drop cap effect */
.post-content-modern :deep(> p:first-of-type::first-letter) {
  float: left;
  font-size: 4.5rem;
  line-height: 0.85;
  margin: 0.1em 0.15em 0 0;
  font-weight: 700;
  color: #3b82f6;
  font-family: "Georgia", serif;
}

/* Floating Action Buttons */
.action-btn-float {
  @apply flex items-center space-x-2 px-4 py-2.5 rounded-full bg-white border-2 border-gray-200 text-gray-600 hover:border-primary-500 hover:text-primary-600 transition-all duration-300 shadow-sm hover:shadow-md;
  transform-origin: center;
}

.action-btn-float.active {
  @apply border-red-500 text-red-600;
}

.action-btn-float:hover:not(:disabled) {
  transform: translateY(-2px);
}

.action-btn-float:active:not(:disabled) {
  transform: translateY(0);
}

/* Comments Section Styling */
.comments-section {
  max-width: 42rem;
  margin: 0 auto;
}

/* Responsive Typography */
@media (max-width: 768px) {
  .post-content-modern {
    font-size: 1rem;
  }

  .post-content-modern :deep(h1) {
    font-size: 2rem;
  }

  .post-content-modern :deep(h2) {
    font-size: 1.75rem;
  }

  .post-content-modern :deep(h3) {
    font-size: 1.25rem;
  }

  .post-content-modern :deep(> p:first-of-type::first-letter) {
    font-size: 3.5rem;
  }
}

/* VSCode Dark Theme - Code Syntax Highlighting */
.post-content-modern :deep(pre) {
  background: #1e1e1e !important;
  border-radius: 6px !important;
  border: 1px solid #484f58 !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
  position: relative;
  margin: 2rem 0 !important;
  padding: 0 !important;
  overflow: hidden;
}

.post-content-modern :deep(pre::before) {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 30px;
  background: #141619;
  z-index: 1;
  border-radius: 5px 5px 0 0;
}

.post-content-modern :deep(pre code) {
  padding: 1.5rem 1rem 1rem 1rem !important;
  display: block !important;
  position: relative;
  z-index: 3;
}

/* Botão de Copiar do VSCode */
.post-content-modern :deep(pre:hover::after) {
  opacity: 1;
}

.post-content-modern :deep(pre::after) {
  content: "";
  position: absolute;
  top: 8px;
  right: 10px;
  width: 12px;
  height: 12px;
  z-index: 4;
  background-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE2IDJDMTYuNTI3MiAyIDE3IDIuNDcyOTEgMTcgM0wxNCAzVjEzQzE0IDEzLjU1MjMgMTMuNTUyMyAxNCAxMyAxNEgzQzIuNDQ3NzE1IDE0IDIyIDEzLjU1MjMgMjIgMTNWOVYyQzIyIDEuNDQ3NzIgMjEuNTUyMiAxIDIxIDFXMTggMUgyQzE3LjQ0NzcgMSAxNyAyLjQ0NzcyIDE3IDNWM1YyQzE3IDIuNDQ3NzEgMTYuNTUyMiAyIDE2IDJaIiBmaWxsPSIjZGJkZGJkIi8+CjxwYXRoIGQ9Ik0xOCA3QzIxIDExLjQyNDIgMTkuNTI3MiAyMC4xMDg2IDE3IDIwQzE1IDIwIDE0IDExIDYgMTJDNSA5LjMgNCA3IDEzIDdIMTk2QzIwIDIxIDI0IDExIDc1KzAiIGZpbGw9IndoaXRlIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPjwvc3ZnPg==);
  background-repeat: no-repeat;
  background-position: center;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.post-content-modern :deep(pre::after:hover) {
  opacity: 1;
}

/* Feedback visual para cópia */
.post-content-modern :deep(pre.copied::after) {
  background-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE2IDJDMTYuNTI3MiAyIDE3IDIuNDcyOTEgMTcgM0wxNCAzVjEzQzE0IDEzLjU1MjMgMTMuNTUyMyAxNCAxMyAxNEgzQzIuNDQ3NzE1IDE0IDIyIDEzLjU1MjMgMjIgMTNWOVYyQzIyIDEuNDQ3NzIgMjEuNTUyMiAxIDIxIDFXMTggMUgyQzE3LjQ0NzcgMSAxNyAyLjQ0NzcyIDE3IDNWM1YyQzE3IDIuNDQ3NzEgMTYuNTUyMiAyIDE2IDJaIiBmaWxsPSIjNGFlZjVhIi8+CjxwYXRoIGQ9Ik0xOCA3QzIxIDExLjQyNDIgMTkuNTI3MiAyMC4xMDg2IDE3IDIwQzE1IDIwIDE0IDExIDYgMTJDNSA5LjMgNCA3IDEzIDdIMTk2QzIwIDIxIDI0IDExIDc1KzAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzRhZWY1YSIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPC9zdmc+);
}

/* Botões de controle macOS */
.post-content-modern :deep(pre) {
  counter-reset: dot;
}

.post-content-modern :deep(pre .dot) {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-left: 4px;
}

.post-content-modern :deep(pre .dot::before) {
  counter-increment: dot;
  content: "";
  display: block;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: currentColor;
}

.post-content-modern :deep(pre .dot:nth-child(1)::before) {
  background: #ff5f56;
}
.post-content-modern :deep(pre .dot:nth-child(2)::before) {
  background: #ffbd2e;
}
.post-content-modern :deep(pre .dot:nth-child(3)::before) {
  background: #27bf41;
}

/* Botões de controle macOS */
.post-content-modern :deep(pre) {
  counter-reset: dot;
}

/* Enhanced inline code with VSCode look */
.post-content-modern :deep(code:not(pre code)) {
  background: rgba(110, 118, 129, 0.4) !important;
  color: #f8f8f2 !important;
  padding: 2px 6px !important;
  border-radius: 4px !important;
  font-family: "SF Mono", Monaco, Inconsolata, Fira Code, "Droid Sans Mono",
    Source Code Pro, Consolas, "Courier New", monospace !important;
  font-size: 0.85em !important;
  line-height: 1.2 !important;
  border: 1px solid rgba(110, 118, 129, 0.3) !important;
}

/* Prism.js VSCode Dark Theme Syntax Coloring */
.post-content-modern :deep(.token.comment),
.post-content-modern :deep(.token.prolog),
.post-content-modern :deep(.token.doctype),
.post-content-modern :deep(.token.cdata) {
  color: #6a737d;
  font-style: italic;
}

.post-content-modern :deep(.token.punctuation) {
  color: #f8f8f2;
}

.post-content-modern :deep(.token.property),
.post-content-modern :deep(.token.tag),
.post-content-modern :deep(.token.constant),
.post-content-modern :deep(.token.symbol),
.post-content-modern :deep(.token.deleted) {
  color: #e06c75;
}

.post-content-modern :deep(.token.boolean),
.post-content-modern :deep(.token.number) {
  color: #d19a66;
}

.post-content-modern :deep(.token.selector),
.post-content-modern :deep(.token.attr-name),
.post-content-modern :deep(.token.string),
.post-content-modern :deep(.token.char),
.post-content-modern :deep(.token.builtin),
.post-content-modern :deep(.token.inserted) {
  color: #98c379;
}

.post-content-modern :deep(.token.operator),
.post-content-modern :deep(.token.entity),
.post-content-modern :deep(.token.url),
.post-content-modern :deep(.language-css .token.string),
.post-content-modern :deep(.style .token.string) {
  color: #56b6c2;
}

.post-content-modern :deep(.token.atrule),
.post-content-modern :deep(.token.attr-value),
.post-content-modern :deep(.token.function),
.post-content-modern :deep(.token.class-name) {
  color: #61aeee;
}

.post-content-modern :deep(.token.keyword) {
  color: #c678dd;
}

.post-content-modern :deep(.token.regex),
.post-content-modern :deep(.token.important),
.post-content-modern :deep(.token.variable) {
  color: #d19a66;
}

/* Ensure code elements have proper contrast (VSCode Dark white text) */
.post-content-modern :deep(pre code) {
  color: #f8f8f2 !important;
}

/* Apple-Style Floating Back Button */
.back-button-floating {
  @apply flex items-center space-x-2 px-4 py-2.5 rounded-full;
  @apply bg-white/80 backdrop-blur-xl shadow-lg;
  @apply text-gray-900 font-medium text-sm;
  @apply transition-all duration-500 ease-out;
  @apply hover:bg-white hover:shadow-xl;
  border: 1px solid rgba(0, 0, 0, 0.06);
  transform-origin: left center;
}

.back-button-floating:hover {
  transform: scale(1.05);
  border-color: rgba(59, 130, 246, 0.3);
}

.back-button-floating:active {
  transform: scale(0.98);
}

/* Text appears on hover */
.back-button-text {
  @apply transition-all duration-300 ease-out;
  max-width: 0;
  opacity: 0;
  overflow: hidden;
  white-space: nowrap;
}

.back-button-floating:hover .back-button-text {
  max-width: 60px;
  opacity: 1;
  margin-left: 0.5rem;
}

/* Enhanced state when scrolled */
.back-button-floating.scrolled {
  @apply shadow-2xl;
  background: rgba(255, 255, 255, 0.95);
  border-color: rgba(0, 0, 0, 0.1);
}

/* Mobile adjustments */
@media (max-width: 640px) {
  .back-button-floating {
    @apply px-3 py-2;
  }

  .back-button-floating svg {
    @apply w-4 h-4;
  }

  .back-button-text {
    @apply text-xs;
  }

  .back-button-floating:hover .back-button-text {
    max-width: 50px;
  }
}

/* Smooth entrance animation */
@keyframes slideInFromLeft {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.back-button-floating {
  animation: slideInFromLeft 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}
</style>
